import { useState } from 'react';
import toast from 'react-hot-toast';

import {
  grantCredits,
  recomputeTeamPeriod,
  settleTeamPeriod,
  type AdminTeamCredits,
  type CreditEntryKind,
} from '@/api-models/admin/credits';
import { formatUsd } from '@/api-models/usage';
import { Badge, type BadgeProps } from '@/ui/Badge';
import { Button } from '@/ui/button/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/Card';
import { Input } from '@/ui/Input';
import { Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow } from '@/ui/Table';

const KIND_VARIANT: Record<CreditEntryKind, BadgeProps['variant']> = {
  grant: 'success',
  revocation: 'warning',
  settlement: 'neutral',
};

const KIND_LABELS: Record<CreditEntryKind, string> = {
  grant: 'Grant',
  revocation: 'Revocation',
  settlement: 'Settlement',
};

function formatStamp(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * The balance, the two repair tools, and the full credit history.
 *
 * Settlements are shown alongside staff grants on purpose. They are where a
 * period's overspend leaves the balance, and a reader who cannot see that has no
 * reason to believe the number at the top.
 */
export function CreditsCard({
  teamId,
  credits,
  onChanged,
}: {
  teamId: string;
  credits: AdminTeamCredits;
  onChanged: () => Promise<unknown>;
}) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [isBusy, setIsBusy] = useState(false);

  const parsed = Number(amount.trim());
  const canSubmit = amount.trim() !== '' && Number.isFinite(parsed) && parsed !== 0 && reason.trim() !== '' && !isBusy;

  async function run(action: () => Promise<unknown>, success: string) {
    setIsBusy(true);
    try {
      await action();
      await onChanged();
      toast.success(success);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setIsBusy(false);
    }
  }

  async function submitGrant() {
    if (!canSubmit) return;
    await run(() => grantCredits(teamId, parsed, reason.trim()), parsed > 0 ? 'Credits granted' : 'Credits revoked');
    setAmount('');
    setReason('');
  }

  async function recompute() {
    setIsBusy(true);
    try {
      const result = await recomputeTeamPeriod(teamId);
      await onChanged();
      toast.success(
        result.drift_micro_usd === 0
          ? 'No drift — the counter matched the ledgers'
          : `Repaired ${formatUsd(Math.abs(result.drift_micro_usd))} of drift`,
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not recompute the period');
    } finally {
      setIsBusy(false);
    }
  }

  async function settle() {
    if (!window.confirm('Close the current period and settle any overspend against credits?')) return;
    await run(() => settleTeamPeriod(teamId), 'Period settled');
  }

  return (
    <>
      <Card padding="none" radius="2xl" className="overflow-hidden">
        <CardHeader className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>Balance</CardTitle>
          <div className="flex gap-2">
            <Button variant="ghost" size={7} onClick={recompute} disabled={isBusy}>
              Recompute period
            </Button>
            <Button variant="ghost" size={7} onClick={settle} disabled={isBusy}>
              Force settle &amp; roll
            </Button>
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Figure label="Allowance" micro={credits.allowance_micro_usd} />
          <Figure label="Spent this period" micro={credits.period_spent_micro_usd} />
          <Figure label="Credits" micro={credits.credit_balance_micro_usd} />
          <Figure
            label="Available"
            micro={credits.available_micro_usd}
            tone={credits.is_exhausted ? 'danger' : 'strong'}
          />
        </CardContent>
        {credits.credit_balance_micro_usd < 0 && (
          <CardContent className="border-t border-line pt-3 text-2xs text-fg-muted">
            The credit balance is negative: a past period overshot its allowance. The debt is repaid from this
            period&rsquo;s allowance rather than blocking the space outright.
          </CardContent>
        )}
      </Card>

      <Card padding="none" radius="2xl" className="overflow-hidden">
        <CardHeader>
          <CardTitle>Credits</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <div className="grid gap-3 sm:grid-cols-[9rem_1fr_auto]">
            <label className="flex flex-col gap-1.5">
              <span className="text-2xs font-medium text-fg-secondary">Amount (USD)</span>
              <Input
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="50 or -10"
                inputMode="decimal"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-2xs font-medium text-fg-secondary">Reason (required)</span>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Invoice #1042 paid by bank transfer"
              />
            </label>
            <div className="flex items-end">
              <Button onClick={submitGrant} disabled={!canSubmit}>
                {parsed < 0 ? 'Revoke' : 'Grant'}
              </Button>
            </div>
          </div>
          <p className="text-2xs text-fg-muted">
            Negative revokes. Every movement is appended to the ledger and cannot be edited or deleted afterwards.
          </p>
        </CardContent>

        {credits.entries.length === 0 ? (
          <CardContent className="border-t border-line text-xs text-fg-muted">No credit movements yet.</CardContent>
        ) : (
          <div className="overflow-x-auto border-t border-line">
            <Table>
              <TableHead>
                <tr>
                  <TableHeaderCell>When</TableHeaderCell>
                  <TableHeaderCell className="w-28">Kind</TableHeaderCell>
                  <TableHeaderCell>Reason</TableHeaderCell>
                  <TableHeaderCell className="w-28 text-right">Amount</TableHeaderCell>
                </tr>
              </TableHead>
              <TableBody>
                {credits.entries.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="whitespace-nowrap text-2xs text-fg-muted tabular-nums">
                      {formatStamp(e.created_at)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={KIND_VARIANT[e.kind]}>{KIND_LABELS[e.kind]}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-fg-secondary">{e.reason}</TableCell>
                    <TableCell
                      className={`text-right text-xs tabular-nums ${
                        e.amount_micro_usd < 0 ? 'text-fg-muted' : 'font-medium text-fg'
                      }`}
                    >
                      {formatUsd(e.amount_micro_usd)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </>
  );
}

function Figure({
  label,
  micro,
  tone = 'normal',
}: {
  label: string;
  micro: number;
  tone?: 'normal' | 'strong' | 'danger';
}) {
  const toneClass = tone === 'danger' ? 'text-danger' : tone === 'strong' ? 'text-fg' : 'text-fg-secondary';
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-2xs text-fg-muted">{label}</span>
      <span className={`text-sm font-medium tabular-nums ${toneClass}`}>{formatUsd(micro)}</span>
    </div>
  );
}
